import { Logtail } from "@logtail/browser";
import algoliasearch from 'algoliasearch';

// Initialize Logtail in both development and production for testing
const logtail = process.env.REACT_APP_LOGTAIL_SOURCE_TOKEN 
  ? new Logtail(process.env.REACT_APP_LOGTAIL_SOURCE_TOKEN)
  : null;

// Debug logging for development
const debugLog = (message, data) => {
  console.log(`🔍 Algolia Logger: ${message}`, data);
};

// Cache for user IP to avoid multiple API calls
let userIPCache = null;

// Function to get user's IP address
const getUserIP = async () => {
  if (userIPCache) {
    return userIPCache;
  }

  try {
    // Try multiple IP services as fallbacks
    const ipServices = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://httpbin.org/ip'
    ];

    for (const service of ipServices) {
      try {
        const response = await fetch(service, { 
          timeout: 2000,
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          let ip = data.ip || data.origin;
          
          if (ip) {
            userIPCache = ip;
            debugLog("User IP obtained", { ip, service });
            return ip;
          }
        }
      } catch (serviceError) {
        debugLog("IP service failed", { service, error: serviceError.message });
        continue;
      }
    }
    
    // Fallback: try to get IP from request headers if available
    if (window.navigator && window.navigator.userAgent) {
      userIPCache = 'unknown';
      return 'unknown';
    }
    
  } catch (error) {
    debugLog("Failed to get user IP", { error: error.message });
    userIPCache = 'unknown';
    return 'unknown';
  }
  
  userIPCache = 'unknown';
  return 'unknown';
};

// Helper function to extract search context information
const extractSearchContext = (requests, options = {}) => {
  const context = {
    requestCount: requests.length,
    indexes: [],
    queries: [],
    totalChars: 0,
    hasUserToken: false,
    hasRuleContexts: false,
    customHeaders: {}
  };

  requests.forEach((request, index) => {
    if (request.indexName) context.indexes.push(request.indexName);
    if (request.query) {
      context.queries.push({
        index: index,
        query: request.query,
        length: request.query.length
      });
      context.totalChars += request.query.length;
    }
    if (request.userToken) context.hasUserToken = true;
    if (request.ruleContexts) context.hasRuleContexts = true;
  });

  // Extract custom headers
  if (options.headers) {
    Object.keys(options.headers).forEach(key => {
      if (key.startsWith('X-Dealmed') || key.startsWith('X-Component')) {
        context.customHeaders[key] = options.headers[key];
      }
    });
  }

  return context;
};

// Simplified search client wrapper that only logs and doesn't modify the original behavior
export function createLoggedAlgoliaClient(appId, apiKey, clientOptions = {}) {
  debugLog("Creating logged Algolia client", { appId, hasApiKey: !!apiKey, clientOptions });
  
  const originalClient = algoliasearch(appId, apiKey, clientOptions);
  debugLog("Original client created successfully", { client: !!originalClient });

  // Create a minimal wrapper that preserves all original functionality
  const wrappedClient = Object.create(originalClient);
  
  // Only wrap the search method
  wrappedClient.search = async function(requests, options = {}) {
    debugLog("Search method called", { requestsCount: requests.length });
    
    // Get user IP for logging
    const userIP = await getUserIP();
    
    // Analyze request types to differentiate between recommendations and search results
    const requestAnalysis = requests.map(req => {
        let searchType = 'unknown';
        let searchCategory = 'general_search';
        
        // Detect search type based on index name and parameters
        if (req.indexName === 'itemsIndex_query_suggestions') {
          searchType = 'query_suggestions';
          searchCategory = 'recommended_suggestions';
        } else if (req.indexName && req.indexName.includes('itemsIndex')) {
          searchType = 'product_search';
          searchCategory = 'search_results';
        }
        
        // Additional detection based on rule contexts and search parameters
        if (req.ruleContexts && req.ruleContexts.includes('autocomplete')) {
          searchCategory = 'autocomplete_suggestions';
        }
        
        // Detect if this might be from recent searches (no specific index but small query)
        if (!req.indexName && req.query && req.query.length > 0) {
          searchType = 'recent_search';
          searchCategory = 'recent_searches';
        }
        
        return {
          index: req.indexName,
          query: req.query,
          query_length: req.query ? req.query.length : 0,
          search_type: searchType,
          search_category: searchCategory,
          has_user_token: !!req.userToken,
          rule_contexts: req.ruleContexts || [],
          hits_per_page: req.hitsPerPage || 0
        };
      });
      
    try {
      // Enhanced logging with search type differentiation
      if (logtail) {
        // Determine primary event type based on the most important search category
        let primaryEventType = 'algolia_search_general';
        const categories = [...new Set(requestAnalysis.map(r => r.search_category))];
        
        if (categories.includes('recommended_suggestions')) {
          primaryEventType = 'algolia_search_recommendations';
        } else if (categories.includes('search_results')) {
          primaryEventType = 'algolia_search_results';
        } else if (categories.includes('autocomplete_suggestions')) {
          primaryEventType = 'algolia_search_autocomplete';
        } else if (categories.includes('recent_searches')) {
          primaryEventType = 'algolia_search_recent';
        }
        
        logtail.info(`Algolia search initiated: ${primaryEventType.replace('algolia_search_', '')}`, {
          event_type: primaryEventType,
          user_ip: userIP,
          requests_count: requests.length,
          queries: requestAnalysis,
          search_summary: {
            has_recommended_suggestions: requestAnalysis.some(r => r.search_category === 'recommended_suggestions'),
            has_search_results: requestAnalysis.some(r => r.search_category === 'search_results'),
            has_autocomplete_suggestions: requestAnalysis.some(r => r.search_category === 'autocomplete_suggestions'),
            has_recent_searches: requestAnalysis.some(r => r.search_category === 'recent_searches'),
            unique_indexes: [...new Set(requestAnalysis.map(r => r.index))],
            total_query_length: requestAnalysis.reduce((sum, r) => sum + r.query_length, 0),
            search_categories: categories
          },
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
      }

      // Execute the actual search
      debugLog("About to call original search");
      const response = await originalClient.search(requests, options);
      debugLog("Original search completed successfully");
      
      // Enhanced logging after successful search
      if (logtail) {
        const responseAnalysis = response.results?.map((result, index) => {
          const originalRequest = requestAnalysis[index] || {};
          return {
            index: result.index,
            search_category: originalRequest.search_category,
            search_type: originalRequest.search_type,
            hits_count: result.nbHits,
            processing_time_ms: result.processingTimeMS,
            query_used: originalRequest.query,
            was_cached: result.processingTimeMS < 1 // Likely cached if < 1ms
          };
        }) || [];
        
        // Determine response event type based on the search categories
        let responseEventType = 'algolia_response_general';
        const responseCategories = [...new Set(responseAnalysis.map(r => r.search_category))];
        
        if (responseCategories.includes('recommended_suggestions')) {
          responseEventType = 'algolia_response_recommendations';
        } else if (responseCategories.includes('search_results')) {
          responseEventType = 'algolia_response_results';
        } else if (responseCategories.includes('autocomplete_suggestions')) {
          responseEventType = 'algolia_response_autocomplete';
        } else if (responseCategories.includes('recent_searches')) {
          responseEventType = 'algolia_response_recent';
        }
        
        logtail.info(`Algolia search completed: ${responseEventType.replace('algolia_response_', '')}`, {
          event_type: responseEventType,
          user_ip: userIP,
          results_count: response.results?.length || 0,
          response_details: responseAnalysis,
          performance_summary: {
            total_processing_time: responseAnalysis.reduce((sum, r) => sum + (r.processing_time_ms || 0), 0),
            total_hits_found: responseAnalysis.reduce((sum, r) => sum + (r.hits_count || 0), 0),
            recommended_suggestions_count: responseAnalysis.filter(r => r.search_category === 'recommended_suggestions').length,
            search_results_count: responseAnalysis.filter(r => r.search_category === 'search_results').length,
            autocomplete_suggestions_count: responseAnalysis.filter(r => r.search_category === 'autocomplete_suggestions').length,
            recent_searches_count: responseAnalysis.filter(r => r.search_category === 'recent_searches').length,
            cached_responses: responseAnalysis.filter(r => r.was_cached).length
          },
          timestamp: new Date().toISOString()
        });
      }

      return response;

    } catch (error) {
      debugLog("Search failed with error", { error: error.message });
      
             // Log error with search context
       if (logtail) {
         // Determine error event type based on what was being searched
         let errorEventType = 'algolia_error_general';
         if (typeof requestAnalysis !== 'undefined') {
           const errorCategories = [...new Set(requestAnalysis.map(r => r.search_category))];
           
           if (errorCategories.includes('recommended_suggestions')) {
             errorEventType = 'algolia_error_recommendations';
           } else if (errorCategories.includes('search_results')) {
             errorEventType = 'algolia_error_results';
           } else if (errorCategories.includes('autocomplete_suggestions')) {
             errorEventType = 'algolia_error_autocomplete';
           } else if (errorCategories.includes('recent_searches')) {
             errorEventType = 'algolia_error_recent';
           }
         }
         
         logtail.error(`Algolia search failed: ${errorEventType.replace('algolia_error_', '')}`, {
           event_type: errorEventType,
           user_ip: userIP,
           error_message: error.message,
           error_name: error.name,
           search_context: typeof requestAnalysis !== 'undefined' ? {
             categories: [...new Set(requestAnalysis.map(r => r.search_category))],
             queries: requestAnalysis.map(r => r.query).filter(Boolean)
           } : null,
           timestamp: new Date().toISOString()
         });
       }

      // Re-throw the error to maintain original behavior
      throw error;
    }
  };
  
  debugLog("Wrapped client created successfully");
  
  return wrappedClient;
}

// Convenience function for standard Dealmed configuration
export function createDealmedAlgoliaClient() {
  debugLog("Creating Dealmed Algolia client", { 
    appId: '9487DJSWRC',
    hasApiKey: !!process.env.REACT_APP_ALGOLIA_API_KEY,
    environment: process.env.NODE_ENV 
  });
  
  // Remove custom headers that cause CORS issues - we'll log them via Logtail instead
  return createLoggedAlgoliaClient(
    '9487DJSWRC', 
    process.env.REACT_APP_ALGOLIA_API_KEY
    // No custom headers to avoid CORS issues
  );
}